export const SITE_LANG_KEY = "ddc-lang";
export const SITE_LANG_EVENT = "ddc-lang-change";

export const SITE_LANGUAGES = [
  { code: "en", label: "English", short: "En" },
  { code: "hi", label: "हिन्दी", short: "Hi" },
] as const;

export type SiteLanguageCode = (typeof SITE_LANGUAGES)[number]["code"];

export function isSiteLanguageCode(value: string | null | undefined): value is SiteLanguageCode {
  return value === "en" || value === "hi";
}

/** Current site UI language from localStorage (header switcher). Defaults to English. */
export function getSiteLanguage(): SiteLanguageCode {
  try {
    const stored = localStorage.getItem(SITE_LANG_KEY);
    if (isSiteLanguageCode(stored)) return stored;
  } catch {
    /* ignore */
  }
  return "en";
}

/** Persist language and notify listeners (chatbot, etc.) site-wide. */
export function setSiteLanguage(code: SiteLanguageCode): void {
  try {
    localStorage.setItem(SITE_LANG_KEY, code);
    document.documentElement.lang = code;
  } catch {
    /* ignore */
  }
  window.dispatchEvent(
    new CustomEvent(SITE_LANG_EVENT, { detail: { code } }),
  );
}

/** Subscribe to header language changes without React context. */
export function subscribeSiteLanguage(
  listener: (code: SiteLanguageCode) => void,
): () => void {
  const onStorage = (e: StorageEvent) => {
    if (e.key === SITE_LANG_KEY && isSiteLanguageCode(e.newValue)) {
      listener(e.newValue);
    }
  };
  const onCustom = (e: Event) => {
    const detail = (e as CustomEvent<{ code?: string }>).detail;
    if (isSiteLanguageCode(detail?.code)) listener(detail.code);
  };
  window.addEventListener("storage", onStorage);
  window.addEventListener(SITE_LANG_EVENT, onCustom);
  return () => {
    window.removeEventListener("storage", onStorage);
    window.removeEventListener(SITE_LANG_EVENT, onCustom);
  };
}
